import { useCallback, useEffect, useState } from 'react';
import { fetchMyClubs, type MyClub } from '../lib/api/myClubs';

// ─────────────────────────────────────────────────────────────────────
// THE CLUBS THIS PLAYER IS ON THE ROSTER OF — for the 📍 selector.
//
// One of four separate concepts in the player app (owner, 2026-10-16):
//
//   account identity    the session (`clubSession` / `memberSession`)
//   player app shell    `MobileApp`, mounted whatever the answers below are
//   club membership     THIS — `GET /me/clubs`, roster rows, never scopes
//   active club context the club the player selected, if any
//
// Memberships are what the selector OFFERS, and nothing more. Reading them
// selects nothing and grants nothing: the active context is a separate,
// explicit choice, and the server checks the roster row for that club on every
// request it is named in.
//
// «NONE» AND «WE COULD NOT ASK» STAY DIFFERENT ANSWERS, as `fetchMyClubs`
// keeps them: `ready` with no clubs is a player who belongs to none, `failed`
// is a question that went unanswered, and the selector says which.
// ─────────────────────────────────────────────────────────────────────

export interface PlayerClubsState {
  /** `idle` when this surface has no memberships to read (not an account). */
  status: 'idle' | 'loading' | 'ready' | 'failed';
  clubs: MyClub[];
  reload: () => void;
}

export function usePlayerClubs(
  enabled: boolean,
  load: typeof fetchMyClubs = fetchMyClubs,
): PlayerClubsState {
  const [state, setState] = useState<{ status: 'loading' | 'ready' | 'failed'; clubs: MyClub[] }>({
    status: 'loading',
    clubs: [],
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setState((s) => ({ status: 'loading', clubs: s.clubs }));
    void load().then((r) => {
      if (cancelled) return;
      setState(r.failed ? { status: 'failed', clubs: [] } : { status: 'ready', clubs: r.clubs });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, attempt, load]);

  const reload = useCallback(() => setAttempt((a) => a + 1), []);
  if (!enabled) return { status: 'idle', clubs: [], reload };
  return { ...state, reload };
}
